import React, { useEffect, useRef, useState } from "react";
import styles from "./BusinessProfileForm.module.scss";
import FormInput from "../FormInput/FormInput";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  getCategories,
  resetProfileURL,
  uploadProfileImage,
} from "../../reducer/profileSlice";
import ProfileFilterInput from "../ProfileFilterInput/ProfileFilterInput";
import toast from "../Toast/toast";
import CreationProfileImage from "components/UI/CreationProfileImage/CreationProfileImage";
import NoImageSVG from "components/SVGComponents/NoImageSVG";
import { selectMapper, serverFiles, compressImage } from "utils/utils";
import UploadSVG from "components/SVGComponents/UploadSVG";
import { API } from "service/Interceptor";
import SelectCustom from "components/common/Select/Select";
import { SelectStyles } from "utils/constants";
import CrossIconSVG from "components/SVGComponents/CrossIconSVG";
import ProfilePicRemoveModal from "components/Modal/ProfilePicRemoveModal";

const BusinessProfileForm = ({ setBusinessProfile }) => {
  const { userProfile, categories } = useSelector((state) => state.userProfile);
  const newCategories = selectMapper(categories);
  const [showPopupModal, setShowPopupModal] = useState(false);

  const dispatch = useDispatch();

  const fileInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setError,
    clearErrors,
    watch,
    formState: { isReady, errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      businessName: "",
      contactPerson: "",
      estYear: "",
      preference: [],
      aboutUs: "",
    },
  });

  const [currentIndustry, setCurrentIndustry] = useState("");

  const {
    fields: industryFields,
    append: appendIndustry,
    remove: removeIndustry,
  } = useFieldArray({
    control,
    name: "industry",
  });

  // =====================================================
  //       HANDLING INDUSTRY INPUT FIELDS SECTION
  // =====================================================
  const handleAddIndustry = () => {
    const WORK_TITLE_MAX_OPTIONS = 3;
    const trimmedIndustry = currentIndustry.trim();

    const isValidFormat = /^[A-Za-z]+(?: [A-Za-z]+)*$/.test(trimmedIndustry);
    const letterCount = trimmedIndustry.replace(/\s/g, "").length;

    if (!isValidFormat || letterCount < 3) {
      toast.error(
        'Please enter a valid industry (e.g., "Software Development", "IT Infrastructure"). Only letters and single spaces allowed. No symbols or numbers.'
      );
      return;
    }

    if (
      currentIndustry.trim() &&
      industryFields.length < WORK_TITLE_MAX_OPTIONS
    ) {
      appendIndustry({ value: currentIndustry.trim() });
      clearErrors("industry");
    } else {
      setError("industry", {
        type: "manual",
        message: `You can add up to ${WORK_TITLE_MAX_OPTIONS} work titles.`,
      });
    }

    setCurrentIndustry("");
  };

  const handleKeyDownAddIndustry = (e) => {
    if (e.key === "Enter" && currentIndustry.trim()) {
      e.preventDefault();
      handleAddIndustry();
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const fileToUpload = await compressImage(file);
      const formData = new FormData();
      formData.append("file", fileToUpload);

      await dispatch(
        uploadProfileImage({ payload: formData, type: "BUSINESS" })
      );
    }
  };

  const handleRemoveImage = async () => {
    // e.stopPropagation();
    // setShowPopupModal(true);
    dispatch(resetProfileURL());
    dispatch(uploadProfileImage({ payload: null, type: "BUSINESS" }));
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
    setShowPopupModal(false);
  };

  useEffect(() => {
    const formData = watch();

    reset({
      businessName:
        userProfile?.companyResponseDto?.name || formData?.businessName,
      contactPerson:
        userProfile?.companyResponseDto?.supportContactName ||
        formData?.contactPerson,
      estYear:
        userProfile?.companyResponseDto?.establishmentYear || formData?.estYear,
      // preference: userProfile?.companyResponseDto?.categoryId && {
      //   value: userProfile?.companyResponseDto?.categoryId,
      //   label: categories?.find(
      //     (s) => s.id === userProfile?.companyResponseDto?.categoryId
      //   )?.name,
      // },
      preference: userProfile?.companyResponseDto?.categories?.length
        ? userProfile?.companyResponseDto?.categories?.map((category) => {
          const matched = categories?.find((cat) => cat?.id === category?.id);
          return {
            value: matched?.id,
            label: matched?.name || "Unknown",
          };
        })
        : formData.preference,
      aboutUs: userProfile?.companyResponseDto?.about || formData?.aboutUs,
      industry:
        (userProfile?.companyResponseDto?.industry?.length > 0 &&
          userProfile?.companyResponseDto?.industry?.map((i) => ({
            value: i,
          }))) ||
        formData.industry,
      about: userProfile?.companyResponseDto?.about || formData?.aboutUs,
    });
  }, [categories]);

  const onSubmit = async (data) => {
    const industry_array = industryFields?.map((item) => item.value);

    const preferences = Array.isArray(data.preference)
      ? data.preference
        ?.map((pref) => {
          const matched = categories?.find((p) => p?.id === pref?.value);
          return matched.id;
        })
        .filter(Boolean)
      : [];

    const payload = {
      name: data.businessName,
      supportContactName: data.contactPerson,
      establishmentYear: parseInt(data.estYear),
      categoryId: preferences || data.preference,
      about: data.aboutUs,
      profileUrl: userProfile?.profileUrl,
      industry: industry_array,
      userType: "BUSINESS",
      websiteUrl: "",
    };

    // console.log(payload);

    try {
      const res = await API.put("api/company", payload);

      if (res?.data?.success) {
        toast.success("Profile updated successfully");
        setTimeout(() => {
          setBusinessProfile(false);
        }, 1000);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    dispatch(getCategories());
  }, [dispatch]);

  return (
    <section className={styles.business_profile}>
      <div
        className={styles.cross_icon}
        onClick={() => setBusinessProfile(false)}
      >
        <CrossIconSVG />
      </div>
      <p className={styles.edit_heading}>Edit Profile</p>

      <section className={styles.business_logo_image}>
        {userProfile?.profileUrl ? (
          <>
            <div className={styles.img_container}>
              <img
                height={70}
                width={70}
                style={{ borderRadius: "100%", border: "2px solid #C8CDF3" }}
                src={serverFiles(userProfile?.profileUrl)}
              />
              <button
                type="button"
                className={styles.reset_photo_btn}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPopupModal(true);
                  // dispatch(resetProfileURL());
                  // if (fileInputRef.current) {
                  //   fileInputRef.current.value = null;
                  // }
                }}
              >
                ✕
              </button>
            </div>
            {/* Profile picture Removal Modal */}
            <ProfilePicRemoveModal
              showPopupModal={showPopupModal}
              setShowPopupModal={setShowPopupModal}
              handleRemoveImage={handleRemoveImage}
            />
          </>
        ) : (
          <NoImageSVG type="profile" height={60} width={60} />
        )}

        <div onClick={handleImageClick}>
          <button className={styles.upload_image_button}>
            <UploadSVG />
            Upload Image
          </button>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageChange}
            style={{ display: "none" }}
          />
        </div>
      </section>
      <form
        onSubmit={handleSubmit((data) => onSubmit(data))}
        className={styles.business_form}
      >
        <section className={styles.business_form_inputs}>
          <FormInput
            label="Business Name"
            register={register}
            id="businessName"
            type="text"
            // required
            placeholder="Enter Business Name"
            error={errors.businessName}
          />
        </section>
        <section className={styles.business_form_inputs}>
          <FormInput
            label="Contact Person"
            register={register}
            id="contactPerson"
            type="text"
            placeholder="Enter Contact Person Name"
            error={errors.contactPerson}
          />
        </section>

        <section className={styles.business_form_inputs}>
          <FormInput
            label="Est Year"
            register={register}
            id="estYear"
            type="text"
            maxLength={4}
            placeholder="Enter Est Year"
            error={errors.estYear}
            validation={{
              required: "Mandatory Field",
              pattern: {
                value: /^[0-9]{4}$/,
                message: "Enter a valid 4-digit year",
              },
              validate: (value) =>
                (parseInt(value, 10) > 1500 &&
                  parseInt(value, 10) <= new Date().getUTCFullYear()) ||
                `Year must be between 1501 and ${new Date().getUTCFullYear()}.`,
            }}
          />
        </section>
        <section className={styles.business_form_inputs}>
          {/* <FormInput
            label="Preference"
            register={register}
            id="preference"
            type="select_2"
            // required
            options={categories}
            validation={{ required: "Preference is Required" }}
            error={errors.preference}
          /> */}

          <div className={styles.reactSelect}>
            <label htmlFor="">Preferences</label>
            <Controller
              name="preference" // Use consistent naming (lowercase)
              control={control}
              rules={{ required: "Mandatory Field" }} // Optional validation
              render={({ field }) => (
                <>
                  <SelectCustom
                    {...field}
                    placeholder="Select Upto 4 Preferences"
                    options={newCategories.filter(
                      (category) =>
                        !field?.value?.some(
                          (f_v) => f_v?.value == category?.value
                        )
                    )}
                    value={[]}
                    // menuIsOpen={undefined}
                    onChange={(selectedOption) => {
                      const PREFERENCE_MAX_OPTIONS = 4;
                      if (
                        Array.isArray(selectedOption) &&
                        field?.value?.length >= PREFERENCE_MAX_OPTIONS
                      ) {
                        setError("preference", {
                          type: "manual",
                          message: `You can add up to ${PREFERENCE_MAX_OPTIONS} preferences.`,
                        });
                        return;
                      }

                      clearErrors("preference");
                      field.onChange([
                        ...(field?.value || []),
                        ...selectedOption,
                      ]);
                    }}
                    styles={SelectStyles}
                    searchable={true}
                    isMulti={true}
                    isDisplay={true}
                    data={field}
                  />
                  {errors.preference && (
                    <span style={{ color: "red", fontSize: ".6rem" }}>
                      {errors.preference.message}
                    </span>
                  )}

                  {/* {Array.isArray(field.value) &&
                            field.value.length > 0 && (
                              <div className={styles.tagsWrapper}>
                                {field.value.map((item, index) => (
                                  <div key={index} className={styles.tag}>
                                    {item.label}
                                    <button
                                      type="button"
                                      className={styles.removeBtn}
                                      onClick={() => {
                                        const updated = field.value.filter(
                                          (_, i) => i !== index
                                        );
                                        field.onChange(updated);
                                      }}
                                    >
                                      X
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )} */}
                </>
              )}
            />
          </div>
        </section>

        <section className={styles.business_form_inputs}>
          <ProfileFilterInput
            label="Work Title"
            subLabel="(Press enter to add work title)"
            id="industry"
            type="add_cards"
            placeholder="e.g Photographer, Writer, Designer"
            register={register}
            value={currentIndustry}
            onChange={(e) => setCurrentIndustry(e.target.value)}
            onKeyDown={handleKeyDownAddIndustry}
            onAdd={handleAddIndustry}
            addedItems={industryFields?.map((field) => field.value)}
            // addedItems={industryFields?.map((field) => {
            //   return field.value
            // })}
            onRemoveItem={(index) => {
              clearErrors("industry");
              return removeIndustry(index);
            }}
          // required={true}
          // validation={{ required: "Skills are required" }}
          // error={errors.skills}
          />
        </section>
        {errors.industry && (
          <span style={{ color: "red", fontSize: ".6rem" }}>
            {errors.industry.message}
          </span>
        )}
        {/* <div className={styles.tagsWrapper}>
          {industryFields.map((field, index) => (
            <div key={index} className={styles.tag}>
              {field.value}
              <button
                type="button"
                onClick={() => {
                  clearErrors("industry");
                  return removeIndustry(index);
                }}
                className={styles.removeBtn}
              >
                X
              </button>
            </div>
          ))}
        </div> */}

        <section className={styles.business_form_inputs}>
          <FormInput
            label="About Us"
            id="aboutUs"
            rows={2}
            type="textarea"
            register={register}
          />
        </section>

        <section className={styles.business_update_button}>
          <button
            // disabled={isReady}
            className={
              // !isReady
              //   ? styles.business_form_submit_btn
              //   : styles.business_form_submit_btn_disabled
              styles.business_form_submit_btn
            }
            type="submit"
          >
            Update
          </button>
        </section>
      </form>
    </section>
  );
};

export default BusinessProfileForm;
